import type { Exercise } from "../types";

export const intermedioExercises: Exercise[] = [
  {
    id: "in-01",
    levelId: "intermedio",
    order: 1,
    title: "Arpegios sin Apoyo",
    subtitle: "Las notas de un acorde, una tras otra: la esencia del acompañamiento",
    category: "arpegios",
    difficulty: "medio",
    type: "sequence",
    methodRef: "Cap. IX - Arpegios (1a Parte), Ej. 44-45",
    description:
      "Un arpegio es tocar las notas de un acorde sucesivamente en lugar de simultaneamente. Es la tecnica que da elegancia al acompañamiento y permite que la melodia florezca entre los bajos.",
    instructions: [
      "Se te mostrara un acorde en el mastil.",
      "Toca cada nota del acorde en secuencia, de grave a agudo.",
      "Sin apoyo: levanta cada dedo despues de tocar.",
    ],
    steps: [
      { id: 1, instruction: "Arpegio de La Mayor: toca cuerda 5 (La) - el bajo", positions: [{ string: 5, fret: 0 }] },
      {
        id: 2,
        instruction: "Ahora cuerda 3 (Mi) - con dedo i",
        positions: [{ string: 5, fret: 0 }, { string: 3, fret: 2 }],
      },
      {
        id: 3,
        instruction: "Cuerda 2 (La) - con dedo m",
        positions: [{ string: 5, fret: 0 }, { string: 3, fret: 2 }, { string: 2, fret: 2 }],
      },
      {
        id: 4,
        instruction: "Cuerda 1 (Do#) - con dedo a",
        positions: [
          { string: 5, fret: 0 },
          { string: 3, fret: 2 },
          { string: 2, fret: 2 },
          { string: 1, fret: 2 },
        ],
      },
      {
        id: 5,
        instruction: "Ahora descendente: 1-2-3-5",
        positions: [
          { string: 1, fret: 2 },
          { string: 2, fret: 2 },
          { string: 3, fret: 2 },
          { string: 5, fret: 0 },
        ],
      },
    ],
    professorHint:
      '"Sin apoyo" significa que cada dedo se levanta despues de pulsar. El sonido se corta limpiamente. Es la base del arpegio clasico.',
    professorNote:
      "El Capitulo IX dedica dos ejercicios a los arpegios sin apoyo. Mas adelante veremos los arpegios con apoyo, que producen un sonido mas conectado (legato).",
    timeEstimate: 6,
    stringsNeeded: [1, 2, 3, 4, 5, 6],
    fretRange: [0, 3],
  },
  {
    id: "in-02",
    levelId: "intermedio",
    order: 2,
    title: "Articulacion y Apoyo Simultaneos",
    subtitle: "Cuando ambas manos bailan al mismo tiempo",
    category: "digitacion",
    difficulty: "medio",
    type: "sequence",
    methodRef: "Cap. VIII - Uso de articulacion y apoyo simultaneos (Ej. 39-43)",
    description:
      "La coordinacion suprema: la mano izquierda apoya (dedos fijos) mientras la derecha articula (pulgar + i-m-a). Este ejercicio sincroniza ambas manos para tocar acordes completos con independencia digital.",
    instructions: [
      "La mano izquierda forma un acorde con dedos apoyados.",
      "La mano derecha articula: pulgar toca el bajo, i-m-a tocan las agudas.",
      "Ambos movimientos ocurren simultaneamente.",
    ],
    steps: [
      {
        id: 1,
        instruction: "Forma Mi Mayor: dedos en cuerdas 5-4-3 (traste II)",
        positions: [{ string: 6, fret: 0 }, { string: 5, fret: 2 }, { string: 4, fret: 2 }, { string: 3, fret: 0 }],
      },
      {
        id: 2,
        instruction: "Pulgar toca cuerda 6 (Mi) mientras i-m-a apoyan 3-2-1",
        positions: [{ string: 6, fret: 0 }, { string: 3, fret: 0 }, { string: 2, fret: 0 }, { string: 1, fret: 0 }],
      },
      {
        id: 3,
        instruction: "Manteniendo el acorde, pulgar toca cuerda 5 (Si)",
        positions: [{ string: 5, fret: 2 }, { string: 4, fret: 2 }, { string: 3, fret: 0 }],
      },
      {
        id: 4,
        instruction: "Transicion a La Mayor: cambia la mano izquierda",
        positions: [{ string: 5, fret: 0 }, { string: 3, fret: 2 }, { string: 2, fret: 2 }, { string: 1, fret: 2 }],
      },
    ],
    professorHint:
      "El truco esta en que la mano izquierda se mueve LENTAMENTE formando el acorde completo, mientras la derecha articula. No cambies de acorde hasta que la mano izquierda este lista.",
    professorNote:
      "El Capitulo VIII introduce la coordinacion simultanea. Es el puente entre la tecnica de mano derecha (Cap. IV) y la izquierda (Cap. III).",
    timeEstimate: 7,
    stringsNeeded: [1, 2, 3, 4, 5, 6],
    fretRange: [0, 3],
  },
  {
    id: "in-03",
    levelId: "intermedio",
    order: 3,
    title: "Escala Cromatica Ascendente",
    subtitle: "Todos los semitonos: el alfabeto completo del mastil",
    category: "escalas",
    difficulty: "medio",
    type: "sequence",
    methodRef: "Cap. X - Ejercicios cromaticos (1a Parte), Ej. 46-49",
    description:
      "La escala cromatica incluye TODOS los trastes: no salta ninguno. Es el ejercicio definitivo para conocer el mastil centimetro a centimetro y desarrollar velocidad digital uniforme.",
    instructions: [
      "Sube por una cuerda tocando cada traste sin excepcion.",
      "Usa los 4 dedos: 1-2-3-4, luego desliza y repite 1-2-3-4.",
      "Mantien el ritmo constante: cada nota tiene el mismo valor.",
    ],
    steps: [
      { id: 1, instruction: "Cuerda 6: Mi (traste 0) con dedo libre", positions: [{ string: 6, fret: 0 }] },
      { id: 2, instruction: "Fa (traste 1) con dedo 1", positions: [{ string: 6, fret: 1 }] },
      { id: 3, instruction: "Fa# (traste 2) con dedo 2", positions: [{ string: 6, fret: 2 }] },
      { id: 4, instruction: "Sol (traste 3) con dedo 3", positions: [{ string: 6, fret: 3 }] },
      { id: 5, instruction: "Sol# (traste 4) con dedo 4", positions: [{ string: 6, fret: 4 }] },
      { id: 6, instruction: "Desliza: La (traste 5) con dedo 1", positions: [{ string: 6, fret: 5 }] },
      { id: 7, instruction: "La# (traste 6) con dedo 2", positions: [{ string: 6, fret: 6 }] },
      { id: 8, instruction: "Si (traste 7) con dedo 3", positions: [{ string: 6, fret: 7 }] },
      { id: 9, instruction: "Do (traste 8) con dedo 4", positions: [{ string: 6, fret: 8 }] },
    ],
    professorHint:
      "Despues del traste 4, desliza el dedo 1 al traste 5 y repite el patron. La escala cromatica no tiene posiciones: es un flujo continuo.",
    professorNote:
      "El Capitulo X tiene 4 ejercicios cromaticos. En el gimnasio lo expandimos para cubrir toda la extension del mastil. Es el ejercicio mas completo de mapa mental.",
    timeEstimate: 6,
    stringsNeeded: [6],
    fretRange: [0, 12],
  },
  {
    id: "in-04",
    levelId: "intermedio",
    order: 4,
    title: "Desplazamientos de Mano Izquierda",
    subtitle: "Mover la mano sin perderse: la habilidad que separa niveles",
    category: "digitacion",
    difficulty: "dificil",
    type: "sequence",
    methodRef: "Cap. XII - Desplazamientos de mano izquierda (Ej. 55)",
    description:
      "El desplazamiento es mover toda la mano a una nueva posicion del mastil manteniendo el contexto musical. Es lo que te permite tocar escalas completas y cambiar de registro sin mirar el diapason.",
    instructions: [
      "Toca una secuencia en la posicion I (trastes 1-4).",
      "Sin interrumpir el sonido, desplaza la mano a la posicion V (trastes 5-8).",
      "Continua la secuencia en la nueva posicion.",
    ],
    steps: [
      {
        id: 1,
        instruction: "Posicion I: toca Sol-La-Si-Do en cuerda 6 (trastes 3-5-7-8)",
        positions: [
          { string: 6, fret: 3 },
          { string: 6, fret: 5 },
          { string: 6, fret: 7 },
          { string: 6, fret: 8 },
        ],
      },
      { id: 2, instruction: "Desplaza a posicion V: dedo 1 en traste 5", positions: [{ string: 6, fret: 5 }] },
      {
        id: 3,
        instruction: "Continua: La-Si-Do#-Re (trastes 5-7-9-10)",
        positions: [
          { string: 6, fret: 5 },
          { string: 6, fret: 7 },
          { string: 6, fret: 9 },
          { string: 6, fret: 10 },
        ],
      },
      { id: 4, instruction: "Desplaza a posicion VIII: dedo 1 en traste 8", positions: [{ string: 6, fret: 8 }] },
      {
        id: 5,
        instruction: "Finaliza: Do-Re-Mi-Fa (trastes 8-10-12-13)",
        positions: [
          { string: 6, fret: 8 },
          { string: 6, fret: 10 },
          { string: 6, fret: 12 },
          { string: 6, fret: 13 },
        ],
      },
    ],
    professorHint:
      'El desplazamiento se hace con un "vuelo" rapido de la mano. Practica primero el movimiento SIN tocar, luego anade las notas.',
    professorNote:
      "El Capitulo XII tiene un solo ejercicio pero es crucial: el desplazamiento es la tecnica que te permite tocar en cualquier parte del mastil sin miedo.",
    timeEstimate: 8,
    stringsNeeded: [6],
    fretRange: [0, 15],
  },
  {
    id: "in-05",
    levelId: "intermedio",
    order: 5,
    title: "Cuerdas Dobles: Intervalos de Tercera",
    subtitle: "Dos notas al mismo tiempo: el primer paso hacia la polifonia",
    category: "teoria",
    difficulty: "dificil",
    type: "click",
    methodRef: "Cap. XI - Cuerdas dobles y acordes de tres y cuatro notas (Ej. 50-54)",
    description:
      "Tocar dos cuerdas simultaneamente crea intervalos. Las terceras son los mas melodicos y forman la base del acompañamiento a dos voces. Este ejercicio te ensena a construir terceras en cualquier posicion.",
    instructions: [
      "Se te dara una nota fundamental.",
      "Encuentra su tercera mayor (4 semitonos arriba) en una cuerda adyacente.",
      "Toca ambas cuerdas simultaneamente.",
    ],
    steps: [
      {
        id: 1,
        instruction: "Do en cuerda 5 traste 3. Encuentra su tercera mayor (Mi) en cuerda 4.",
        positions: [{ string: 5, fret: 3 }, { string: 4, fret: 2 }],
        answers: ["Do", "Mi"],
        hint: "Tercera mayor = 4 semitonos. Do -> Do# -> Re -> Re# -> Mi",
      },
      {
        id: 2,
        instruction: "Sol en cuerda 6 traste 3. Tercera mayor (Si) en cuerda 5.",
        positions: [{ string: 6, fret: 3 }, { string: 5, fret: 2 }],
        answers: ["Sol", "Si"],
      },
      {
        id: 3,
        instruction: "Re en cuerda 4 traste 0. Tercera mayor (Fa#) en cuerda 3.",
        positions: [{ string: 4, fret: 0 }, { string: 3, fret: 2 }],
        answers: ["Re", "Fa#"],
      },
      {
        id: 4,
        instruction: "La en cuerda 3 traste 2. Tercera mayor (Do#) en cuerda 2.",
        positions: [{ string: 3, fret: 2 }, { string: 2, fret: 2 }],
        answers: ["La", "Do#"],
      },
    ],
    professorHint:
      "La tercera mayor es el intervalo mas dulce. En la guitarra, entre cuerdas adyacentes (excepto 3-2) la tercera esta 1 traste atras en la cuerda superior.",
    professorNote:
      "El Capitulo XI trabaja cuerdas dobles, triples y acordes de 4 notas. Es la transicion hacia la polifonia completa.",
    timeEstimate: 7,
    stringsNeeded: [1, 2, 3, 4, 5, 6],
    fretRange: [0, 5],
  },
];
